/*
 * App Store and Google Play download buttons for the public site.
 * They link straight to the store listings.
 */
import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/storeLinks";

/** Apple logo glyph. */
export function AppleLogo({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 384 512" fill="currentColor" aria-hidden>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

/** Google Play logo glyph, in its four brand colours. */
export function GooglePlayLogo({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 512 512" aria-hidden>
      <path fill="#FFD400" d="M357.4 252.5L78.3 480.3c.6.4 1.2.7 1.9 1.1 4.7 2.6 10.5 2.7 15.8-.4l294.6-170 -33.2-58.5z" />
      <path fill="#FF3A44" d="M357.4 259.5L78.3 31.7c.6-.4 1.2-.7 1.9-1.1 4.7-2.6 10.5-2.7 15.8.4l294.6 170-33.2 58.5z" />
      <path fill="#48FF48" d="M68 11c-3.7 4.4-5.6 11-5.6 19.5v451c0 8.5 1.9 15.2 5.6 19.5l289.4-234L68 11z" />
      <path fill="#3BCCFF" d="M390.6 188.2L334.2 220.2 390.6 322.8l67.4-39c20.5-12 20.5-31.7 0-43.7l-67.4-51.9z" />
    </svg>
  );
}

const badgeClass =
  "inline-flex items-center gap-3 h-14 pl-4 pr-5 rounded-xl bg-black text-white no-underline border border-white/20 hover:bg-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2";

/**
 * The pair of store download buttons.
 * @param className extra classes for the wrapping row
 */
export default function StoreBadges({ className = "" }: { className?: string }) {
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={badgeClass}
        aria-label="Download H-Plate on the App Store"
      >
        <AppleLogo className="w-6 h-6" />
        <span className="flex flex-col leading-none text-left">
          <span className="text-[11px] font-medium text-white/80">Download on the</span>
          <span className="text-[19px] font-semibold tracking-tight mt-1">App Store</span>
        </span>
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={badgeClass}
        aria-label="Get H-Plate on Google Play"
      >
        <GooglePlayLogo className="w-6 h-6" />
        <span className="flex flex-col leading-none text-left">
          <span className="text-[11px] font-medium uppercase tracking-wide text-white/80">Get it on</span>
          <span className="text-[19px] font-semibold tracking-tight mt-1">Google Play</span>
        </span>
      </a>
    </div>
  );
}
